// packages/main/app/web/components-v2/UnifiedImageDialog/AIGenerateTab.tsx
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React, { useEffect } from 'react';
import { useMultipleAttachmentsQuery } from '../../../../queries/Attachments/useMultipleAttachmentsQuery';
import { AIStarsIcon } from '../../../components/svgs/icons/AIStars';
import { Button } from '../../Button';
import { Checkbox } from '../../Checkbox';
import { THEME } from '../../Theme/theme';
import { ImageRow } from './components/ImageRow';
import { useApplyBrandingToImages } from './hooks/applyBranding';
import { styleSheet } from './styles';

interface BulkRebrandingTabProps {
	impersonationContext?: Api.IImpersonationContext;
	bulkRebrandingImages?: Api.IFileAttachment[];
	onBulkRebrandingImagesSelected?: (attachments: Api.IFileAttachment[]) => void;
	onOpenChange: (open: boolean) => void;
}

export function BulkRebrandingTab({
	bulkRebrandingImages,
	impersonationContext,
	onBulkRebrandingImagesSelected,
	onOpenChange,
}: BulkRebrandingTabProps) {
	const [selectedImages, setSelectedImages] = React.useState<Api.IFileAttachment[]>([]);

	useEffect(() => {
		if (bulkRebrandingImages) {
			setSelectedImages(bulkRebrandingImages);
		}
	}, [bulkRebrandingImages]);

	const handleImageSelected = (image: Api.IFileAttachment) => {
		setSelectedImages(prev => {
			if (prev.includes(image)) {
				return prev.filter(i => i.id !== image.id);
			}
			return [...prev, image];
		});
	};

	const { applyBranding, isGenerating, resultImages, failedImages } = useApplyBrandingToImages({
		impersonationContext,
	});

	const [selectedResultImages, setSelectedResultImages] = React.useState<
		{
			fileAttachmentId: string;
			mimeType: string;
			sourceFileAttachmentId: string;
		}[]
	>([]);

	const imagesToGenerate = React.useMemo(() => {
		return bulkRebrandingImages?.filter(
			image => !selectedResultImages.some(r => r.sourceFileAttachmentId === image.id)
		);
	}, [bulkRebrandingImages, selectedResultImages]);

	const generatedAttachmentsIds = React.useMemo(() => {
		return selectedResultImages.map(image => image.fileAttachmentId);
	}, [selectedResultImages]);

	const generatedAttachmentsQuery = useMultipleAttachmentsQuery({
		ids: generatedAttachmentsIds,
		enabled: generatedAttachmentsIds.length > 0,
		impersonationContext,
	});

	const brandedAttachments = React.useMemo(() => {
		const attachments = generatedAttachmentsQuery
			.filter(query => query.isSuccess)
			.flatMap(query => query.data ?? ([] as Api.IFileAttachment[]));

		// Deduplicate by attachment ID to prevent duplicates
		const uniqueAttachments = new Map<string, Api.IFileAttachment>();
		attachments.forEach(attachment => {
			if (attachment?.id) {
				uniqueAttachments.set(attachment.id, attachment);
			}
		});

		return Array.from(uniqueAttachments.values());
	}, [generatedAttachmentsQuery]);

	const isLoadingAttachments = React.useMemo(() => {
		return generatedAttachmentsQuery.some(query => query.isLoading);
	}, [generatedAttachmentsQuery]);

	const handleApplyBranding = () => {
		const attachmentIds = selectedImages
			.filter(image => !selectedResultImages.some(r => r.sourceFileAttachmentId === image.id))
			.map(image => image.id);

		applyBranding({ attachmentIds });
	};

	useEffect(() => {
		if (resultImages && resultImages.length > 0) {
			setSelectedResultImages(prev => {
				// Create a map of existing results by sourceFileAttachmentId for quick lookup
				const existingMap = new Map(prev.map(r => [r.sourceFileAttachmentId, r]));

				// Add/update with new results
				resultImages.forEach(newResult => {
					existingMap.set(newResult.sourceFileAttachmentId, newResult);
				});

				return Array.from(existingMap.values());
			});
		}
	}, [resultImages]);

	return (
		<div className={css(styleSheet.bulkRebrandingContainer)}>
			<div className={css(styleSheet.bulkRebrandingHeader)}>
				<h4>Apply my Branding</h4>
			</div>

			<div className={css(styleSheet.imageSelectionContainer)}>
				<div>
					<Button
						size='sm'
						variant='secondary'
						onClick={() => handleApplyBranding()}
						disabled={imagesToGenerate.length === 0 || isGenerating}
						isLoading={isGenerating}
					>
						<AIStarsIcon stopColorOne={THEME.colors.primary.main} stopColorTwo={THEME.colors.primary.main} />
						Apply my Branding
					</Button>

					{failedImages > 0 && (
						<span className={css(styleSheet.failedImagesText)}>
							{failedImages} {failedImages === 1 ? 'image' : 'images'} failed, you can try again by clicking the button
							above
						</span>
					)}
				</div>

				<div className={css(styleSheet.imagesSelectionList)}>
					{imagesToGenerate.map(image => (
						<div key={image.id} className={css(styleSheet.imageSelectionItem)}>
							<img src={image.url} alt={image.fileName} className={css(styleSheet.imageSelectionItemImage)} />

							<label className={css(styleSheet.checkboxLabel)}>
								<Checkbox
									checked={selectedImages.includes(image)}
									onCheckedChange={() => handleImageSelected(image)}
									disabled={isGenerating || isLoadingAttachments}
								/>
								Select Image
							</label>
						</div>
					))}
				</div>
			</div>

			<div className={css(styleSheet.resultContainer)}>
				{selectedResultImages.map(image => (
					<div key={image.fileAttachmentId}>
						<ImageRow
							selected={selectedImages.includes(image)}
							setSelected={handleImageSelected}
							originalAttachment={bulkRebrandingImages?.find(b => b?.id === image.sourceFileAttachmentId)}
							brandedAttachment={brandedAttachments?.find(b => b?.id === image.fileAttachmentId)}
							onCancel={() =>
								setSelectedResultImages(prev => prev.filter(i => i.fileAttachmentId !== image.fileAttachmentId))
							}
						/>
					</div>
				))}
			</div>

			<div className={css(styleSheet.modalActionsContainer)}>
				<Button
					variant='primary'
					size='sm'
					onClick={() => {
						onBulkRebrandingImagesSelected?.([...brandedAttachments, ...imagesToGenerate]);
					}}
					disabled={isLoadingAttachments || isGenerating}
				>
					Save
				</Button>

				<Button variant='secondary' size='sm' onClick={() => onOpenChange(false)}>
					Cancel
				</Button>
			</div>
		</div>
	);
}
